import { Tooltip } from '@mui/material';
import { AccessTime, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';

type MandateStatusWarningProps = {
  hasMandate: boolean;
  status?: number | null;
  /** Texten för makulerat medgivande skiljer sig mellan medlem och månadsgivare. */
  canceledTextKey?: string;
};

/**
 * Sant när dragningen inte kommer att bli av. Används för att stryka över nästa dragning och för
 * att räkna de rader som kräver en åtgärd.
 *
 * Ny och de två väntande lägena räknas inte hit, där är medgivandet på väg någonstans. Inte heller
 * medlemmar utan kopplat medgivande: dragningen görs ändå och lyckas om medgivandet finns hos
 * Bankgirot, även när det inte är registrerat hos oss.
 */
export const mandateBlocksPayment = (hasMandate: boolean, status?: number | null) =>
  hasMandate &&
  (status === AutogiroMandateStatusEnum.NotApproved || status === AutogiroMandateStatusEnum.Error || status === AutogiroMandateStatusEnum.Canceled);

/**
 * Varningsikonen för medgivandets status, gemensam för kopplade medlemmar och kopplade
 * månadsgivare.
 *
 * Rött betyder att dragningen inte blir av och att någon behöver göra något. Gult betyder att
 * medgivandet inte är på väg till Bankgirot, antingen för att det saknas eller för att det inte
 * är skickat. Grå klocka betyder att det är på väg och att ingen kan påskynda det.
 *
 * Ny visas som gult och inte som klocka, eftersom workern bara skickar signerade medgivanden.
 * Ett osignerat medgivande blir alltså aldrig skickat och skulle annars se ut att vara på väg.
 */
export const MandateStatusWarning = (props: MandateStatusWarningProps) => {
  const { hasMandate, status, canceledTextKey = 'invoice:autogiro.mandateCanceledWarning' } = props;
  const { t } = useTranslation(['invoice']);

  if (!hasMandate) {
    return (
      <Tooltip title={t('invoice:autogiro.noMandateWarning')} arrow placement='left'>
        <Warning color='warning' sx={{ fontSize: 20 }} />
      </Tooltip>
    );
  }

  if (status === AutogiroMandateStatusEnum.Canceled) {
    return (
      <Tooltip title={t(canceledTextKey)} arrow placement='left'>
        <Warning color='error' sx={{ fontSize: 20 }} />
      </Tooltip>
    );
  }

  if (status === AutogiroMandateStatusEnum.New) {
    return (
      <Tooltip title={t('invoice:autogiro.mandateNewWarning')} arrow placement='left'>
        <Warning color='warning' sx={{ fontSize: 20 }} />
      </Tooltip>
    );
  }

  if (status === AutogiroMandateStatusEnum.PendingApproval) {
    return (
      <Tooltip title={t('invoice:autogiro.mandatePendingApprovalWarning')} arrow placement='left'>
        <AccessTime sx={{ fontSize: 20, color: 'text.disabled' }} />
      </Tooltip>
    );
  }

  if (status === AutogiroMandateStatusEnum.PendingCancel) {
    return (
      <Tooltip title={t('invoice:autogiro.mandatePendingCancelWarning')} arrow placement='left'>
        <AccessTime sx={{ fontSize: 20, color: 'text.disabled' }} />
      </Tooltip>
    );
  }

  if (status !== AutogiroMandateStatusEnum.Approved) {
    return (
      <Tooltip title={t('invoice:autogiro.mandateNotApprovedWarning')} arrow placement='left'>
        <Warning color='error' sx={{ fontSize: 20 }} />
      </Tooltip>
    );
  }

  return null;
};
